import React, { useState, useEffect, Fragment } from 'react';
import { canManageProjects } from "../../../lib/permissions.js";
import { Menu, Transition } from "@headlessui/react";
import { useAuthContext, useAuthToggleContext } from "../../providers/AuthProvider.jsx";
import { Link } from "react-router-dom";
import axios from "axios";

const DropdownUser2 = () => {
    const authContext = useAuthContext();
    const { handleLogout } = useAuthToggleContext();
    const [dataProfileUser, setDataProfileUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (authContext && authContext.isAuth) {
            setIsLoading(true);
            axios.get('data_profile_user/')
                .then(response => {
                    setDataProfileUser(response.data);
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching user profile:', error);
                    setIsLoading(false);
                });
        }
    }, [authContext.isAuth]);

    if (!authContext.isAuth) {
        return (
            <Link
                to="/login"
                className="btn btn-primary btn-sm hover:btn-primary-focus transition-all duration-200 shadow-lg hover:shadow-xl"
            >
                <LoginIcon className="w-4 h-4 mr-2" />
                Iniciar sesión
            </Link>
        );
    }

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="group flex items-center gap-2  rounded-xl hover:bg-base-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20">
                <div className="relative">
                    {isLoading ? (
                        <div className="w-10 h-10 rounded-full bg-base-300 animate-pulse"></div>
                    ) : dataProfileUser?.foto_perfil ? (
                        <img
                            alt="Foto de perfil"
                            src={dataProfileUser.foto_perfil}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-base-300 group-hover:ring-primary/30 transition-all duration-200"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neutral to-neutral-focus flex items-center justify-center text-neutral-content font-semibold text-sm ring-2 ring-base-300 group-hover:ring-primary/30 transition-all duration-200">
                            {getInitials(dataProfileUser?.name)}
                        </div>
                    )}

                    {/* Indicador de estado online */}
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full ring-2 ring-base-100"></div>
                </div>

                {/* Información del usuario (visible en pantallas grandes) */}
                <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-base-content truncate max-w-32">
                        {dataProfileUser?.name || 'Usuario'}
                    </p>
                    <p className="text-xs text-base-content/60 capitalize">
                        {authContext.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </p>
                </div>

                {/* Chevron icon */}
                <ChevronDownIcon className="w-4 h-4 text-base-content/60 group-hover:text-base-content transition-colors duration-200" />
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95 -translate-y-1"
                enterTo="transform opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100 translate-y-0"
                leaveTo="transform opacity-0 scale-95 -translate-y-1"
            >
                <Menu.Items className="absolute right-0 mt-3 w-72 origin-top-right bg-base-100 rounded-2xl shadow-2xl ring-1 ring-black/5 focus:outline-none z-50 overflow-hidden backdrop-blur-sm">
                    {/* Header del perfil */}
                    <div className="px-6 py-4 bg-gradient-to-r from-neutral/5 to-primary/5 border-b border-base-300">
                        <div className="flex items-center gap-3">
                            {dataProfileUser?.foto_perfil ? (
                                <img
                                    src={dataProfileUser.foto_perfil}
                                    alt="Foto de perfil"
                                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20"
                                />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neutral to-neutral-focus flex items-center justify-center text-neutral-content font-bold">
                                    {getInitials(dataProfileUser?.name)}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base-content truncate">
                                    {dataProfileUser?.name || 'Usuario'}
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    @{dataProfileUser?.public_username || 'usuario'}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                    <div className="w-2 h-2 bg-success rounded-full"></div>
                                    <span className="text-xs text-base-content/60">En línea</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navegación principal */}
                    <div className="p-2">
                        <Menu.Item>
                            {({ active }) => (
                                <Link
                                    to="/profile/general"
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        active
                                            ? 'bg-primary text-primary-content shadow-md transform scale-[1.02]'
                                            : 'text-base-content hover:bg-base-200'
                                    }`}
                                >
                                    <div className={`p-2 rounded-lg ${
                                        active ? 'bg-primary-content/20' : 'bg-base-300'
                                    }`}>
                                        <IconProfile className="w-4 h-4" />
                                    </div>
                                    <span className="flex-1">Mi Perfil</span>
                                    <ChevronRightIcon className="w-4 h-4 opacity-60" />
                                </Link>
                            )}
                        </Menu.Item>

                        {(authContext.role === "admin" || canManageProjects(authContext)) && (
                            <Menu.Item>
                                {({ active }) => (
                                    <Link
                                        to={authContext.role === "admin" ? "/admin/general" : "/admin/proyectos"}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                            active
                                                ? 'bg-secondary text-secondary-content shadow-md transform scale-[1.02]'
                                                : 'text-base-content hover:bg-base-200'
                                        }`}
                                    >
                                        <div className={`p-2 rounded-lg ${
                                            active ? 'bg-secondary-content/20' : 'bg-base-300'
                                        }`}>
                                            <AdminIcon className="w-4 h-4" />
                                        </div>
                                        <span className="flex-1">{authContext.role === "admin" ? "Panel de Administración" : "Panel comercial"}</span>
                                        <div className="px-2 py-1 bg-warning/20 text-warning text-xs font-semibold rounded-full">
                                            {authContext.role === "admin" ? "ADMIN" : "CRM"}
                                        </div>
                                    </Link>
                                )}
                            </Menu.Item>
                        )}

                        {/* Opciones adicionales */}
                        {/*<div className="border-t border-base-300 my-2"></div>

                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        active
                                            ? 'bg-info text-info-content shadow-md transform scale-[1.02]'
                                            : 'text-base-content hover:bg-base-200'
                                    }`}
                                    onClick={() => console.log('Configuración')}
                                >
                                    <div className={`p-2 rounded-lg ${
                                        active ? 'bg-info-content/20' : 'bg-base-300'
                                    }`}>
                                        <SettingsIcon className="w-4 h-4" />
                                    </div>
                                    <span className="flex-1">Configuración</span>
                                </button>
                            )}
                        </Menu.Item>

                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        active
                                            ? 'bg-neutral text-neutral-content shadow-md transform scale-[1.02]'
                                            : 'text-base-content hover:bg-base-200'
                                    }`}
                                    onClick={() => console.log('Ayuda')}
                                >
                                    <div className={`p-2 rounded-lg ${
                                        active ? 'bg-neutral-content/20' : 'bg-base-300'
                                    }`}>
                                        <HelpIcon className="w-4 h-4" />
                                    </div>
                                    <span className="flex-1">Ayuda y soporte</span>
                                </button>
                            )}
                        </Menu.Item>*/}
                    </div>

                    {/* Logout section */}
                    <div className="p-2 border-t border-base-300 bg-base-50">
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={handleLogout}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        active
                                            ? 'bg-error text-error-content shadow-md transform scale-[1.02]'
                                            : 'text-error hover:bg-error/10'
                                    }`}
                                >
                                    <div className={`p-2 rounded-lg ${
                                        active ? 'bg-error-content/20' : 'bg-error/20'
                                    }`}>
                                        <IconLogout className="w-4 h-4" />
                                    </div>
                                    <span className="flex-1">Cerrar Sesión</span>
                                </button>
                            )}
                        </Menu.Item>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 bg-base-200/50 border-t border-base-300">
                        <p className="text-xs text-base-content/60 text-center">
                            Sesión activa • {new Date().toLocaleDateString()}
                        </p>
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

// Iconos SVG
const IconLogout = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M16.5 3.75a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5V15a.75.75 0 0 0-1.5 0v3.75a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5.25a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3V9A.75.75 0 1 0 9 9V5.25a1.5 1.5 0 0 1 1.5-1.5h6ZM5.78 8.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 0 0 0 1.06l3 3a.75.75 0 0 0 1.06-1.06l-1.72-1.72H15a.75.75 0 0 0 0-1.5H4.06l1.72-1.72a.75.75 0 0 0 0-1.06Z" clipRule="evenodd" />
    </svg>
);

const IconProfile = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
    </svg>
);

const LoginIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 0 0 6 5.25v13.5a1.5 1.5 0 0 0 1.5 1.5h6a1.5 1.5 0 0 0 1.5-1.5V15a.75.75 0 0 1 1.5 0v3.75a3 3 0 0 1-3 3h-6a3 3 0 0 1-3-3V5.25a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3V9A.75.75 0 0 1 15 9V5.25a1.5 1.5 0 0 0-1.5-1.5h-6Zm10.72 4.72a.75.75 0 0 1 1.06 0l3 3a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H9a.75.75 0 0 1 0-1.5h10.94l-1.72-1.72a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
    </svg>
);

const AdminIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z" clipRule="evenodd" />
        <path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.487l-.115.04c-.567.2-1.156.349-1.764.44Z" />
    </svg>
);

const SettingsIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 0 0-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 0 0-2.282.819l-.922 1.597a1.875 1.875 0 0 0 .432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 0 0 0 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 0 0-.432 2.385l.922 1.597a1.875 1.875 0 0 0 2.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.570.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 0 0 2.28-.819l.923-1.597a1.875 1.875 0 0 0-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 0 0 0-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 0 0-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 0 0-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 0 0-1.85-1.567h-1.843ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" clipRule="evenodd" />
    </svg>
);

const HelpIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z" clipRule="evenodd" />
    </svg>
);

const ChevronRightIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clipRule="evenodd" />
    </svg>
);

export default DropdownUser2;